// RoomSession — the application-layer reducer driving an active room.
//
// Inputs:
//   - validated `ServerEvent` from the transport (decoded by `WSClient`)
//   - local UI commands (join request, send chat, leave)
// Outputs:
//   - `RoomState` (passed to React via Context)
//   - `ChatEntry[]` log
//   - outbound `ClientEvent`s sent through the injected `Transport`
//
// Kept synchronous and free of React imports so it stays unit-testable.
// (architecture.md §10 / §16)

import type {
  ChatMessage,
  CustomCursor,
  RoomMember,
  RoomMemberLeft,
  RoomWelcome,
  ServerEvent,
  SystemAnnouncePosted,
} from '@oekaki/protocol';

import type { ChatEntry, RoomState } from '../domain/RoomState.js';

/** UI-visible status of the join handshake. */
export type SessionPhase =
  | { readonly t: 'idle' }
  | { readonly t: 'joining'; readonly roomId: string; readonly displayName: string }
  | { readonly t: 'joined'; readonly state: RoomState }
  /**
   * Phase 5: WS dropped while we were `joined`. The Provider is busy
   * re-resolving the dispatcher / opening a fresh socket / sending
   * `room.resume`. UI shows a "再接続中…" banner; the prior `RoomState`
   * is kept so the canvas / member list / chat history stays visible
   * (we just can't draw outbound while the socket is down).
   */
  | {
      readonly t: 'reconnecting';
      /** The room state from the previous welcome — kept so the UI can render. */
      readonly state: RoomState;
      /** Attempt counter, 1-based. */
      readonly attempt: number;
      /**
       * Wall-clock epoch ms when the next reconnect attempt is scheduled.
       * Lets the UI show a countdown if it wants; MVP just shows "再接続中…".
       */
      readonly nextAttemptAt: number;
    }
  | {
      readonly t: 'rejected';
      readonly reason: string;
      readonly message?: string;
    }
  | {
      /**
       * Phase 7 Task 7-4: We received `room.member-left { reason: 'kick',
       * uid === self.uid }` — the local user has been kicked from the room.
       * The session is dead; the SessionProvider suppresses the
       * auto-reconnect dance (otherwise we'd just be re-kicked) and the
       * App renders `KickedScreen` with a re-entry CTA.
       *
       * `roomId` lets the screen offer "もう一度入室" (re-attempt the same
       * room) without the user re-typing the URL. `kickerDisplayName` is
       * resolved from the prior `state.members[]` and passed through so
       * the screen can say「{kicker} さんによってキックされました」.
       */
      readonly t: 'kicked';
      readonly roomId: string;
      readonly kickerDisplayName: string;
    }
  | { readonly t: 'left' };

/**
 * Phase 7 Task 7-6: メンテナンス予告のクライアント側 view 状態.
 *
 * `scheduled` だけ持ち、`countdown` は短命なので最後に受けた `remainingSec` と
 * その受信時刻で表現する. `cancelled` で両者を null に戻す.
 */
export interface MaintenanceViewState {
  /** `system.maintenance.scheduled` 受信時のペイロード. */
  readonly scheduled: {
    readonly startAt: number;
    readonly durationMs?: number;
    readonly reason?: string;
  } | null;
  /** 直近 countdown. UI は `remainingSec` を表示する. */
  readonly countdown: {
    readonly remainingSec: number;
    /** 受信時の epoch ms (UI 内 stale 判定に使える). */
    readonly receivedAt: number;
  } | null;
}

/** Aggregated session view — `phase` + chat log + maintenance banner state. */
export interface SessionView {
  readonly phase: SessionPhase;
  readonly chat: readonly ChatEntry[];
  /**
   * Free-form log of system-level events (errors, maintenance pings).
   * MVP renders these as inline lines in the chat panel.
   *
   * `level` (optional) は ChatPanel の色分けに使う:
   * - `'info'` (= announce.posted severity=info): 通常 (グレー、既存挙動)
   * - `'warning'` (= maintenance.scheduled / announce severity=warning): 橙
   * - `'critical'` (= maintenance.countdown / announce severity=critical): 赤
   * 未指定は info 相当のグレーで表示.
   */
  readonly system: readonly {
    readonly ts: number;
    readonly text: string;
    readonly level?: 'info' | 'warning' | 'critical';
  }[];
  /**
   * Phase 7 Task 7-6: メンテナンス予告の banner / countdown UI 用状態.
   * scheduled/countdown は完全に分離した state として持つ (system 行は
   * 履歴ログ用、banner は最新値のみ反映する).
   */
  readonly maintenance: MaintenanceViewState;
  /**
   * Phase 9.6: service-provider からの全体お知らせ broadcast の active list.
   *
   * - `system.announce.posted` を受信したら `id` で upsert (= 同 id を受け直したら
   *   置き換え) + postedAt 昇順で並べる.
   * - `system.announce.cleared { id }` で対象を除外.
   * - **transient 規約**: ring buffer / snapshot に乗らない event なので、
   *   reconnect / resume では server 側が welcome 直後に active 全件を unicast で
   *   再送する設計. client 側は localStorage 等に永続化しない (= reload で消えても OK).
   * - **client safeguard**: 受信時点で `now >= expiresAt` なら無視する.
   * - **max 10 件**: server 側で cap、client 側でも保険 (= over 10 件来たら最新 10 件のみ).
   */
  readonly activeAnnounces: readonly SystemAnnouncePosted[];
  /**
   * カスタムカーソル (custom-cursor-2026-09-19.md §2.3): 他メンバーが
   * カーソルエディタを**編集中**の transient state, uid → 現在のドラフト画素。
   *
   * `member.cursor-changed` (= 確定状態) とは別管理: 確定分は
   * `RoomState.members[].cursor` に乗る (server-stamped `seq` があり、
   * welcome/resume-ok で再構築される永続 member 状態の一部)。一方こちらは
   * **seq を持たない完全 transient** な broadcast (`member.cursor-drafted`)
   * の最新値だけを保持する — session buffer にも welcome にも乗らない
   * (§2.3 「draft は welcome に含めない」)。
   *
   * `cursor: null` の drafted broadcast (= 編集終了/キャンセル) を受けたら
   * そのエントリを削除する (= 「編集中」ではなくなる。表示は
   * `members[].cursor` の確定値に戻る)。
   */
  readonly cursorDrafts: Readonly<Record<string, CustomCursor>>;
}

export const INITIAL_SESSION_VIEW: SessionView = {
  phase: { t: 'idle' },
  chat: [],
  system: [],
  maintenance: { scheduled: null, countdown: null },
  activeAnnounces: [],
  cursorDrafts: {},
};

/** Phase 9.6: client-side safeguard で stack できる最大数. server cap と一致. */
const MAX_ACTIVE_ANNOUNCES = 10;

const upsertAnnounce = (
  active: readonly SystemAnnouncePosted[],
  next: SystemAnnouncePosted,
): readonly SystemAnnouncePosted[] => {
  const without = active.filter((a) => a.id !== next.id);
  // postedAt 昇順 (= server stamped 順) で並べる.
  const merged = [...without, next].sort((a, b) => a.postedAt - b.postedAt);
  // safeguard: 10 件超は最も古いものから切り捨て (= 新しい順に最大 10 件).
  if (merged.length <= MAX_ACTIVE_ANNOUNCES) return merged;
  return merged.slice(merged.length - MAX_ACTIVE_ANNOUNCES);
};

const removeAnnounce = (
  active: readonly SystemAnnouncePosted[],
  id: string,
): readonly SystemAnnouncePosted[] => active.filter((a) => a.id !== id);

const upsertMember = (members: readonly RoomMember[], next: RoomMember): RoomMember[] => {
  const existing = members.findIndex((m) => m.uid === next.uid);
  if (existing < 0) return [...members, next];
  const copy = members.slice();
  copy[existing] = next;
  return copy;
};

const removeMember = (members: readonly RoomMember[], uid: string): RoomMember[] =>
  members.filter((m) => m.uid !== uid);

/**
 * Build the system-line text for a `room.member-left` event.
 *
 * Reason enum is fixed by the protocol (`'leave' | 'kick' | 'disconnect'`,
 * see protocol-events.md §2.6 / `roomMemberLeftSchema`). Old Java client
 * only had a single 「退室しました」 line for everyone — we keep the base
 * wording and append a short qualifier so the chat log can distinguish a
 * normal leave from a kick or a connection drop without becoming noisy.
 *
 * Phase 7 Task 7-4: `reason === 'kick'` で kickerName が解決できれば
 * 「{kicker} が {target} をキックしました」を使う. server が古い (kickerUid
 * を載せない) / kicker が members[] に居ない (== ghost broadcast) のときは
 * 既存の「{target} が退室しました (キック)」に fallback する.
 *
 * The `string` fallback covers the (currently unreachable) case of a
 * future protocol-events.md addition reaching an old client.
 */
const leaveText = (
  targetDisplayName: string,
  reason: RoomMemberLeft['reason'] | string,
  kickerDisplayName: string | null,
): string => {
  switch (reason) {
    case 'leave':
      return `${targetDisplayName} が退室しました`;
    case 'kick':
      return kickerDisplayName !== null
        ? `${kickerDisplayName} が ${targetDisplayName} をキックしました`
        : `${targetDisplayName} が退室しました (キック)`;
    case 'disconnect':
      return `${targetDisplayName} が退室しました (切断)`;
    default:
      return `${targetDisplayName} が退室しました`;
  }
};

const fromWelcome = (msg: RoomWelcome): RoomState => ({
  roomId: msg.room.id,
  uid: msg.uid,
  resumeToken: msg.resumeToken,
  displayName: msg.displayName,
  permissions: msg.permissions,
  isRom: msg.isRom,
  roomName: msg.room.name,
  canvas: {
    width: msg.room.settings.canvasSize.width,
    height: msg.room.settings.canvasSize.height,
  },
  members: msg.members,
  lastSeq: msg.lastSeq,
  // Phase 7 Task 7-5: clearOpen は room.welcome.room の必須 field.
  clearOpen: msg.room.clearOpen,
});

const pushChat = (chat: readonly ChatEntry[], msg: ChatMessage): ChatEntry[] => [
  ...chat,
  {
    seq: msg.seq,
    uid: msg.uid,
    displayName: msg.displayName,
    text: msg.text,
    ts: msg.ts,
  },
];

/**
 * Apply a server event to the current view. Returns a NEW view object on
 * every call (or the same reference if the event is irrelevant) so React's
 * shallow-equal `useReducer` flow can skip renders cleanly.
 */
export const applyServerEvent = (view: SessionView, ev: ServerEvent): SessionView => {
  switch (ev.t) {
    case 'room.welcome': {
      const state = fromWelcome(ev);
      // custom-cursor-2026-09-19.md §2.3 の穴埋め (fix/stale-cursor-draft):
      // draft は完全 transient (welcome に同梱されない) なので、fresh join
      // 時点で前の接続から持ち越した `cursorDrafts` が残っている理由がない。
      // 明示的にリセットする (通常は INITIAL_SESSION_VIEW 由来で既に空だが、
      // 同一タブでの再 join 等を防御的にカバーする).
      return { ...view, phase: { t: 'joined', state }, cursorDrafts: {} };
    }

    case 'room.rejected': {
      return {
        ...view,
        phase: { t: 'rejected', reason: ev.reason, message: ev.message },
      };
    }

    case 'room.resume-ok': {
      // Only meaningful when we already have a room state to refresh
      // (`reconnecting` is the typical entry; `joined` covers the rare
      // race where the WS resume happens to land before the reducer
      // saw `closed`).
      const prev =
        view.phase.t === 'reconnecting' || view.phase.t === 'joined' ? view.phase.state : null;
      if (prev === null) return view;
      // Re-sync members from the fresh roster the server sent. Permissions
      // may have changed (Phase 6 admin flows) — pick up the new value.
      const refreshed: RoomState = {
        ...prev,
        uid: ev.uid,
        permissions: ev.permissions,
        members: ev.members,
        lastSeq: Math.max(prev.lastSeq, ev.lastSeq),
      };
      // fix/stale-cursor-draft: resume-ok も `room.welcome` と同じ理由で
      // `cursorDrafts` をリセットする. 切断中に相手が draft null (編集終了) を
      // 送れずにタブを閉じた/接続が切れた場合、こちらが再接続するまで
      // 「編集中」表示が残ってしまう穴を塞ぐ (= 再接続の瞬間に必ず一掃).
      return {
        ...view,
        phase: { t: 'joined', state: refreshed },
        cursorDrafts: {},
      };
    }

    case 'room.member-joined': {
      if (view.phase.t !== 'joined') return view;
      const next: RoomMember = {
        uid: ev.uid,
        displayName: ev.displayName,
        isRom: ev.isRom,
        // custom-cursor-2026-09-19.md §2.3: 通常は `null`/absent (fresh join)。
        // ResumeService 由来の再合流 broadcast だけ前回のカーソルを引き継ぐ。
        cursor: ev.cursor ?? null,
      };
      const state = view.phase.state;
      // Push a system line so ChatPanel surfaces 「○○ が入室しました」.
      // Server-side broadcaster excludes the originator (Broadcaster.ts /
      // JoinService.ts), so self-join never reaches us — no filtering needed.
      // Phase 7 Task 7-1 (申し送り d304081 / protocol-events.md §2.5).
      return {
        ...view,
        phase: {
          t: 'joined',
          state: {
            ...state,
            members: upsertMember(state.members, next),
            lastSeq: Math.max(state.lastSeq, ev.seq),
          },
        },
        system: [...view.system, { ts: ev.ts, text: `${ev.displayName} が入室しました` }],
      };
    }

    case 'room.member-left': {
      if (view.phase.t !== 'joined') return view;
      const state = view.phase.state;
      // Resolve displayName from the *current* members[] before we drop the
      // entry. Falls back to '不明なユーザー' if the row isn't there (e.g.
      // a stale broadcast after a snapshot resync).
      const leaving = state.members.find((m) => m.uid === ev.uid);
      const targetName = leaving?.displayName ?? '不明なユーザー';
      // Phase 7 Task 7-4: resolve kicker displayName for the system-line
      // text. `kickerUid` is only present when reason === 'kick' (server
      // stamps it conditionally), and we still defensively guard the
      // lookup — a ghost broadcast (kicker already gone via snapshot resync)
      // falls back to the kicker-less wording.
      const kickerName =
        ev.reason === 'kick' && ev.kickerUid !== undefined
          ? (state.members.find((m) => m.uid === ev.kickerUid)?.displayName ?? null)
          : null;
      const text = leaveText(targetName, ev.reason, kickerName);

      // fix/stale-cursor-draft: leave/kick/disconnect が正規の "編集終了"
      // (`member.cursor-draft null`) より先に届くケース (タブを閉じた/WS が
      // 先に切れた) をカバーする. 編集終了 broadcast がその後に届いても
      // no-op になるだけなので二重に消しても問題ない.
      const { [ev.uid]: _droppedDraft, ...cursorDrafts } = view.cursorDrafts;

      // Phase 7 Task 7-4: self-kick → transition to the dedicated `kicked`
      // phase so KickedScreen renders + SessionProvider suppresses the
      // auto-reconnect dance. We still push the system line so any UI
      // surface that re-mounts the prior state (rare) sees consistent copy.
      if (ev.reason === 'kick' && ev.uid === state.uid) {
        return {
          ...view,
          phase: {
            t: 'kicked',
            roomId: state.roomId,
            // Use the explicit kicker if we can resolve it; otherwise the
            // screen falls back to a kicker-less message.
            kickerDisplayName: kickerName ?? '管理者',
          },
          system: [...view.system, { ts: ev.ts, text }],
          cursorDrafts,
        };
      }

      // Server excludes the originator on leave/kick/disconnect broadcasts
      // (MemberService.ts / ResumeTokenSweeper.ts), so the leaving user
      // never observes their own row — same rationale as member-joined.
      return {
        ...view,
        phase: {
          t: 'joined',
          state: {
            ...state,
            members: removeMember(state.members, ev.uid),
            lastSeq: Math.max(state.lastSeq, ev.seq),
          },
        },
        system: [...view.system, { ts: ev.ts, text }],
        cursorDrafts,
      };
    }

    case 'chat.message': {
      const phase = view.phase;
      const lastSeq = phase.t === 'joined' ? Math.max(phase.state.lastSeq, ev.seq) : ev.seq;
      const nextPhase: SessionPhase =
        phase.t === 'joined' ? { t: 'joined', state: { ...phase.state, lastSeq } } : phase;
      return { ...view, phase: nextPhase, chat: pushChat(view.chat, ev) };
    }

    case 'member.rom-changed': {
      if (view.phase.t !== 'joined') return view;
      const state = view.phase.state;
      const members = state.members.map((m) => (m.uid === ev.uid ? { ...m, isRom: ev.rom } : m));
      const isRom = ev.uid === state.uid ? ev.rom : state.isRom;
      return {
        ...view,
        phase: {
          t: 'joined',
          state: { ...state, members, isRom, lastSeq: Math.max(state.lastSeq, ev.seq) },
        },
      };
    }

    case 'member.name-changed': {
      if (view.phase.t !== 'joined') return view;
      const state = view.phase.state;
      // Resolve the OLD displayName from the *current* members[] before we
      // overwrite it (mirror of the room.member-left branch above).
      // Falls back to '不明なユーザー' if the row isn't there (e.g. a stale
      // broadcast after a snapshot resync).
      const previous = state.members.find((m) => m.uid === ev.uid);
      const oldName = previous?.displayName ?? '不明なユーザー';
      const members = state.members.map((m) =>
        m.uid === ev.uid ? { ...m, displayName: ev.displayName } : m,
      );
      const displayName = ev.uid === state.uid ? ev.displayName : state.displayName;
      // Push a system line so ChatPanel surfaces 「○○ が △△ に名前を変更しました」.
      // Server-side broadcaster does NOT exclude the originator on
      // member.name-changed (Phase 7 Task 7-2 確定方針 — UX confirmation),
      // so self-rename also produces a system line. This matches旧版的踏襲.
      return {
        ...view,
        phase: {
          t: 'joined',
          state: {
            ...state,
            members,
            displayName,
            lastSeq: Math.max(state.lastSeq, ev.seq),
          },
        },
        system: [
          ...view.system,
          { ts: ev.ts, text: `${oldName} が ${ev.displayName} に名前を変更しました` },
        ],
      };
    }

    case 'member.cursor-changed': {
      // custom-cursor-2026-09-19.md §2.3: 確定カーソルの反映. member.name-changed
      // と同じ形 (self も含め全員へ broadcast、seq を伴う永続 member 状態)。
      // system 行は出さない (絵チャの雑談ログを汚さない、名前変更ほど「他人に
      // 知らせたい」性質の操作でもないため — 作者判断)。
      if (view.phase.t !== 'joined') return view;
      const state = view.phase.state;
      const members = state.members.map((m) =>
        m.uid === ev.uid ? { ...m, cursor: ev.cursor } : m,
      );
      // 確定を受けたら、その uid の「編集中」表示は終了扱いにする (通常は
      // エディタが閉じる際に cursor-draft null を先に送るので既に消えている
      // はずだが、broadcast の到着順が前後した場合の防御的整合).
      const { [ev.uid]: _dropped, ...cursorDrafts } = view.cursorDrafts;
      return {
        ...view,
        phase: {
          t: 'joined',
          state: { ...state, members, lastSeq: Math.max(state.lastSeq, ev.seq) },
        },
        cursorDrafts,
      };
    }

    case 'member.cursor-drafted': {
      // custom-cursor-2026-09-19.md §2.3: 完全 transient (seq なし). lastSeq は
      // 触らない. `cursor: null` = 編集終了/キャンセルなのでエントリを削除する
      // (= 参加者リスト / NameLabelOverlay は members[].cursor の確定値に戻る)。
      if (ev.cursor === null) {
        if (!(ev.uid in view.cursorDrafts)) return view;
        const { [ev.uid]: _dropped, ...cursorDrafts } = view.cursorDrafts;
        return { ...view, cursorDrafts };
      }
      return {
        ...view,
        cursorDrafts: { ...view.cursorDrafts, [ev.uid]: ev.cursor },
      };
    }

    case 'system.error': {
      return {
        ...view,
        system: [
          ...view.system,
          {
            ts: Date.now(),
            text: `system error (${ev.code})${ev.message ? `: ${ev.message}` : ''}`,
          },
        ],
      };
    }

    case 'system.maintenance.scheduled': {
      // Phase 7 Task 7-6: banner state を更新 + system 行に履歴を残す.
      const reason = ev.reason ? ` — ${ev.reason}` : '';
      const startText = new Date(ev.startAt).toLocaleString('ja-JP');
      const durationMin =
        typeof ev.durationMs === 'number' ? Math.ceil(ev.durationMs / 60_000) : null;
      const durationText = durationMin !== null ? `、約${durationMin}分間` : '';
      return {
        ...view,
        maintenance: {
          // countdown は最新 broadcast だけ持ちたいので、新しい scheduled が
          // 来たら countdown は捨てる (= 次の countdown push が来たら受け直す).
          scheduled: {
            startAt: ev.startAt,
            ...(typeof ev.durationMs === 'number' ? { durationMs: ev.durationMs } : {}),
            ...(typeof ev.reason === 'string' ? { reason: ev.reason } : {}),
          },
          countdown: null,
        },
        system: [
          ...view.system,
          {
            ts: Date.now(),
            text: `メンテナンス予定 (${startText}${durationText})${reason}`,
            level: 'warning',
          },
        ],
      };
    }

    case 'system.maintenance.countdown': {
      // banner の最新 remainingSec を更新. system 行にも履歴は残す.
      // countdown は停止直前 = critical 相当.
      return {
        ...view,
        maintenance: {
          scheduled: view.maintenance.scheduled,
          countdown: { remainingSec: ev.remainingSec, receivedAt: Date.now() },
        },
        system: [
          ...view.system,
          { ts: Date.now(), text: `メンテまで残り ${ev.remainingSec}秒`, level: 'critical' },
        ],
      };
    }

    case 'system.maintenance.cancelled': {
      // Phase 7 Task 7-6: banner state を全クリア + system 行にメモ.
      // 解除通知は info 相当 (= 警告解除なので強調しない).
      return {
        ...view,
        maintenance: { scheduled: null, countdown: null },
        system: [
          ...view.system,
          { ts: Date.now(), text: 'メンテナンス予定が取消されました', level: 'info' },
        ],
      };
    }

    case 'system.announce.posted': {
      // Phase 9.6: service-provider 全体お知らせ broadcast.
      // - TTL 切れ (= 受信時点で `now >= expiresAt`) は client 側で無視 (= server
      //   sweeper との race 時の保険、reconnect 時の unicast 再送で過去分が
      //   届くケースも同じ判定で弾く).
      // - id 単位で upsert (= 同 id を再受信したら置き換え、postedAt 昇順 sort).
      // - safeguard で max 10 件 cap.
      // - system 行にも履歴を残す (transient なので reload で消えるのは OK).
      //   level は severity をそのまま流用 (info/warning/critical).
      const nowMs = Date.now();
      if (nowMs >= ev.expiresAt) return view;
      const severity = ev.severity ?? 'info';
      return {
        ...view,
        activeAnnounces: upsertAnnounce(view.activeAnnounces, ev),
        system: [...view.system, { ts: nowMs, text: `お知らせ: ${ev.message}`, level: severity }],
      };
    }

    case 'system.announce.cleared': {
      // Phase 9.6: 単一 id 対象の取下げ. 該当 id が無ければ no-op (= 既に
      // expire で client 側から消えてる / 別 tab で既に処理済み 等).
      // ChatPanel への「取下げられました」表示はユーザ指示で不要 (= UI ノイズ).
      if (!view.activeAnnounces.some((a) => a.id === ev.id)) return view;
      return {
        ...view,
        activeAnnounces: removeAnnounce(view.activeAnnounces, ev.id),
      };
    }

    case 'whiteboard.clear': {
      // Phase 7 Task 7-5: clear は drawing engine が
      // `routeBroadcastToEngine` 経由で処理 (whiteboardClearBroadcastSchema を
      // 受けて canvas を全消し). reducer 側の責務は:
      //   1. lastSeq を更新
      //   2. system 行に「○○ がキャンバスをクリアしました」を push
      //      (Task 7-1 / 7-4 の系統と同じ chat 通知). originator も含めて
      //      全員に流れる echo-as-ack 方式なので、Server-side で originator 除外
      //      しない (server.WhiteboardService 仕様).
      if (view.phase.t !== 'joined') return view;
      const state = view.phase.state;
      const actor = state.members.find((m) => m.uid === ev.uid);
      const actorName = actor?.displayName ?? '不明なユーザー';
      return {
        ...view,
        phase: {
          t: 'joined',
          state: { ...state, lastSeq: Math.max(state.lastSeq, ev.seq) },
        },
        system: [...view.system, { ts: ev.ts, text: `${actorName} がキャンバスをクリアしました` }],
      };
    }

    case 'whiteboard.restore': {
      // Slice 3 (past-canvas-new-document-2026-07-05.md §4.1): 過去キャンバス
      // 復元 broadcast。キャンバスの実差し替え (種 snapshot の再ロード) は
      // SessionProvider が welcome / resume-ok と同じ hydration 経路で行う。
      // reducer 側の責務は `whiteboard.clear` と同型:
      //   1. lastSeq を更新
      //   2. system 行「○○ が過去のキャンバスを復元しました」を push
      //      (echo-as-ack 方式で originator も含め全員に流れる)。
      if (view.phase.t !== 'joined') return view;
      const state = view.phase.state;
      // 荒らし対策の自動の戻し (auto-response-2026-10-23.md) は uid が 'system' で届く。
      // 名前を引かず「自動で復元しました」と出す (不明なユーザー表示にしない)。
      const text =
        ev.uid === 'system'
          ? '荒らし対策のため、キャンバスを自動で復元しました'
          : `${state.members.find((m) => m.uid === ev.uid)?.displayName ?? '不明なユーザー'} が過去のキャンバスを復元しました`;
      return {
        ...view,
        phase: {
          t: 'joined',
          state: { ...state, lastSeq: Math.max(state.lastSeq, ev.seq) },
        },
        system: [...view.system, { ts: ev.ts, text }],
      };
    }

    case 'whiteboard.imagepaste-rejected': {
      // 2026-06-14: imagepaste の拒否は **チャット (view.system) ではなくキャンバス
      // 中央のトースト**に出すよう移設した。貼付時はユーザがキャンバスを注視しており、
      // チャットの system 行は気付かれにくいため。reducer はもう view.system に積まない
      // (= no-op、view をそのまま返す)。トースト表示は `useImagepaste` が transport の
      // `whiteboard.imagepaste-rejected` を直接購読して `rejectToast` を立て、
      // `CanvasStage` の `ImagepasteToastOverlay` が描画する。reason→日本語マップは
      // `application/imagepasteReject.ts` に集約 (旧 reasonText の移設先)。
      //
      // seq は持たないイベントなので lastSeq の追従も不要 (= 完全 no-op)。
      return view;
    }

    case 'admin.kick-rejected': {
      // Phase 7 Task 7-9: surface a system-line so the actor sees why their
      // kick attempt failed (e.g. wrong target / target is also admin / lost
      // perm mid-flight). Mirrors the `whiteboard.imagepaste-rejected` branch
      // above. `admin.kick-ack` itself stays silent because success is
      // already observable via `room.member-left { reason: 'kick' }` which
      // pushes a system-line in the member-left branch.
      const reasonText: Record<string, string> = {
        'no-permission': 'キックの権限がありません',
        'target-not-found': 'キック対象が見つかりません',
        'cannot-kick-self': '自分自身をキックすることはできません',
        'cannot-kick-admin': '管理者は別の管理者をキックできません',
      };
      const text =
        reasonText[ev.reason] ??
        `キックが拒否されました (${ev.reason})${ev.message ? `: ${ev.message}` : ''}`;
      return {
        ...view,
        system: [...view.system, { ts: Date.now(), text }],
      };
    }

    // ─── stroke / whiteboard.imagepaste / admin.kick-ack ─────────────────────
    // Phase 3 前半: drawing-engine への委譲ポイント (stub では console.log に流す)。
    // ここでは RoomState の更新は行わない (lastSeq の追従だけ)。
    //
    // Phase 7 Task 7-7: `whiteboard.imagepaste` (broadcast) は drawing engine
    // が Task 7-8 で `routeBroadcastToEngine` 経由で処理する. reducer 側は
    // lastSeq lift のみ. `whiteboard.imagepaste-token` も lastSeq は持たない
    // が、unicast response として system 行に出すこともしない (UI 反映は
    // Task 7-8 で SessionProvider 側の listener が担う).
    case 'stroke.start':
    case 'stroke.segment':
    case 'stroke.end':
    // conflict-aware Undo (protocol-events.md §1.1): `stroke.cancelled` is a
    // server-stamped tombstone with its own `seq` (f1 — appended to the
    // session buffer / stroke log like any other drawing event), so it
    // advances `lastSeq` the same way stroke.*/imagepaste do. The actual
    // bbox-local rewind is applied by the drawing engine
    // (`ingestUndoBroadcast`, wired in SessionProvider), not here.
    case 'stroke.cancelled':
    case 'whiteboard.imagepaste':
    // 範囲の移動・コピー (`whiteboard.region`) も seq を持つ broadcast。漏らすと resume が崩れる。
    case 'whiteboard.region':
    // 範囲の移動・コピーの取り消し (tombstone) も seq を持つ broadcast (`stroke.cancelled` と同じ)。
    case 'whiteboard.region-cancelled':
    case 'whiteboard.imagepaste-token':
    case 'admin.kick-ack': {
      const phase = view.phase;
      if (phase.t !== 'joined') return view;
      // stroke.* / whiteboard.* broadcasts carry seq; admin.kick-ack carries
      // seq too. Update lastSeq when the field exists.
      const seq = 'seq' in ev && typeof ev.seq === 'number' ? ev.seq : undefined;
      if (seq === undefined) return view;
      return {
        ...view,
        phase: {
          t: 'joined',
          state: { ...phase.state, lastSeq: Math.max(phase.state.lastSeq, seq) },
        },
      };
    }

    default:
      return view;
  }
};

/** Marks the session as voluntarily left (post-`room.member-left` etc.). */
export const markLeft = (view: SessionView): SessionView => ({
  ...view,
  phase: { t: 'left' },
});

/**
 * Transition to `reconnecting` while the Provider re-resolves the
 * dispatcher / opens a fresh socket. Caller passes the `RoomState` from
 * before the drop so the UI can keep rendering members / chat / canvas.
 * No-op when we were never `joined` (a drop during the initial handshake
 * must surface as 'rejected', not silently retry).
 */
export const markReconnecting = (
  view: SessionView,
  attempt: number,
  nextAttemptAt: number,
): SessionView => {
  // Pull a state out of either current phase variant; otherwise no-op.
  const state =
    view.phase.t === 'joined'
      ? view.phase.state
      : view.phase.t === 'reconnecting'
        ? view.phase.state
        : null;
  if (state === null) return view;
  return {
    ...view,
    phase: { t: 'reconnecting', state, attempt, nextAttemptAt },
  };
};

/** Marks the session as 'joining' (after sending `room.join`). */
export const markJoining = (
  view: SessionView,
  roomId: string,
  displayName: string,
): SessionView => ({
  ...view,
  phase: { t: 'joining', roomId, displayName },
});

/**
 * 入室あいことば不一致 (`room.rejected { reason: 'invalid-entry-password' }`)
 * 用: `joining` phase から `idle` に巻き戻す. ErrorScreen (`rejected` phase)
 * には落とさず、`RootSwitch` の URL 駆動分岐 (= `ReJoinScreen`) に戻して
 * あいことば入力欄の下でエラーを再表示させる (SessionProvider 側が
 * `entryPasswordRejection` state で表示名 / エラーメッセージを引き継ぐ).
 */
export const markIdle = (view: SessionView): SessionView => ({
  ...view,
  phase: { t: 'idle' },
});

/**
 * Phase 7 Task 7-9: ローカルでチャット欄 (chat + system) をクリアする.
 *
 * `/clear` slash command の実体. **キャンバスのクリアではない** (那は
 * `whiteboard.clear` で server に送る別経路、`ToolPanel` の「全消し」ボタンに
 * 集約済). 純粋なローカル UI 操作で server には何も送らない.
 *
 * `phase` は維持する (joined のままなら joined のまま、reconnecting なら
 * reconnecting のまま). `maintenance` banner も別 state として維持される
 * (system 行に履歴が出ていただけで、banner の `scheduled` / `countdown` は
 * 触らない).
 */
export const clearChatLog = (view: SessionView): SessionView => ({
  ...view,
  chat: [],
  system: [],
});
